/**
 * Shared SPA-navigation observer.
 *
 * No native event fires for `history.pushState` / `replaceState`, so they must be
 * monkey-patched to detect client-side route changes. BOTH the semantic-event tap
 * (`observ.session.navigate`) and rich page-view tracking (`app.page_view`) need
 * that signal — patching `history` independently in each would double-wrap the
 * global and make restore order-dependent. This module patches ONCE, fans out to
 * any number of subscribers, and restores the originals only when the last one
 * unsubscribes (ref-counted).
 *
 * Best-effort and reversible; never throws. If the `history` methods can't be
 * patched (frozen / exotic env), `popstate` still drives subscribers.
 */

/** A history navigation, tagged by what triggered it. */
export type HistoryNavigation = { type: 'pushState' | 'replaceState' | 'popstate' }

/** Listener invoked AFTER the navigation has applied (so `location` is updated). */
type NavListener = (nav: HistoryNavigation) => void

const subscribers = new Set<NavListener>()
let patched = false
let originalPush: History['pushState'] | null = null
let originalReplace: History['replaceState'] | null = null
let popStateHandler: (() => void) | null = null

function notify(type: HistoryNavigation['type']): void {
  for (const fn of subscribers) {
    try {
      fn({ type })
    } catch {
      /* swallow — a subscriber must never break navigation */
    }
  }
}

function patch(): void {
  if (patched || typeof window === 'undefined' || typeof history === 'undefined') return

  try {
    originalPush = history.pushState
    originalReplace = history.replaceState
    history.pushState = function (this: History, ...args: Parameters<History['pushState']>): void {
      originalPush?.apply(this, args)
      notify('pushState')
    }
    history.replaceState = function (
      this: History,
      ...args: Parameters<History['replaceState']>
    ): void {
      originalReplace?.apply(this, args)
      notify('replaceState')
    }
  } catch {
    // Roll back any partial patch — popstate below still works.
    if (originalPush) history.pushState = originalPush
    if (originalReplace) history.replaceState = originalReplace
    originalPush = null
    originalReplace = null
  }

  popStateHandler = (): void => notify('popstate')
  window.addEventListener('popstate', popStateHandler)
  patched = true
}

function unpatch(): void {
  if (!patched) return
  try {
    if (originalPush) history.pushState = originalPush
    if (originalReplace) history.replaceState = originalReplace
    if (popStateHandler) window.removeEventListener('popstate', popStateHandler)
  } catch {
    /* swallow — teardown must never throw */
  }
  originalPush = null
  originalReplace = null
  popStateHandler = null
  patched = false
}

/**
 * Subscribe to SPA navigations (pushState / replaceState / popstate). Patches the
 * `history` API on the first subscriber and returns an unsubscribe fn; the
 * originals are restored once the last subscriber leaves. No-op without a DOM.
 */
export function onHistoryNavigation(listener: NavListener): () => void {
  if (typeof window === 'undefined') return () => {}
  subscribers.add(listener)
  patch()
  return () => {
    subscribers.delete(listener)
    if (subscribers.size === 0) unpatch()
  }
}

/**
 * @internal Test-only. Drop all subscribers and restore `history`, so one test's
 * patch can't leak into the next.
 */
export function resetHistoryNav(): void {
  subscribers.clear()
  unpatch()
}
